import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

/**
 * Service pour l'upload de vidéos vers Firebase Storage
 */
export class VideoUploadService {
  
  /**
   * Upload une seule vidéo vers Firebase Storage
   * @param file - Le fichier vidéo à uploader
   * @param path - Le chemin dans Firebase Storage (ex: 'posts', 'profiles')
   * @returns Promise<string> - L'URL de téléchargement de la vidéo
   */
  static async uploadVideo(file: File, path: string): Promise<string> {
    try {
      console.log(`🚀 Début upload vidéo vers ${path}...`);
      
      // Validation du fichier
      if (!file.type.startsWith('video/')) {
        throw new Error('Le fichier doit être une vidéo');
      }
      
      // Limite de taille (50MB pour les vidéos)
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('La taille de la vidéo ne doit pas dépasser 50MB');
      }
      
      // Créer un nom de fichier unique
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2);
      const extension = file.name.split('.').pop();
      const fileName = `${timestamp}_${randomId}.${extension}`;
      
      console.log(`📁 Nom du fichier: ${fileName}`);
      
      // Référence vers le fichier dans Firebase Storage
      const storageRef = ref(storage, `${path}/${fileName}`);
      
      // Upload du fichier avec métadonnées
      const metadata = {
        contentType: file.type,
        customMetadata: {
          uploadedAt: new Date().toISOString(),
          originalName: file.name,
          size: file.size.toString()
        }
      };
      
      console.log(`⬆️ Upload en cours...`);
      const snapshot = await uploadBytes(storageRef, file, metadata);
      console.log(`✅ Vidéo uploadée avec succès: ${path}/${fileName}`);
      
      // Récupérer l'URL de téléchargement
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log(`🔗 URL récupérée: ${downloadURL}`);
      
      return downloadURL;
    } catch (error) {
      console.error('❌ Erreur upload vidéo:', error);
      throw error;
    }
  }

  /**
   * Upload multiple vidéos pour un post (limité à 1 vidéo par post)
   * @param videos - Array des fichiers vidéos à uploader
   * @returns Promise<string[]> - Array des URLs de téléchargement
   */
  static async uploadPostVideos(videos: File[]): Promise<string[]> {
    try {
      if (videos.length === 0) return [];
      
      // Limite de 1 vidéo par post pour éviter les problèmes de performance
      if (videos.length > 1) {
        throw new Error('Maximum 1 vidéo autorisée par post');
      }
      
      console.log(`🚀 Upload de ${videos.length} vidéo(s) pour un post...`);
      
      // Upload toutes les vidéos en série (pas en parallèle pour éviter la surcharge)
      const urls: string[] = [];
      for (const video of videos) {
        const url = await this.uploadVideo(video, 'posts');
        urls.push(url);
      }
      
      console.log(`✅ ${urls.length} vidéo(s) uploadée(s) avec succès`);
      return urls;
    } catch (error) {
      console.error('❌ Erreur upload vidéos post:', error);
      throw error;
    }
  }

  /**
   * Valide un fichier vidéo (type, taille, durée, etc.)
   * @param file - Le fichier à valider
   * @returns Promise<{ isValid: boolean; error?: string; duration?: number }>
   */
  static async validateVideoFile(file: File): Promise<{ isValid: boolean; error?: string; duration?: number }> {
    // Vérifier si c'est une vidéo
    if (!file.type.startsWith('video/')) {
      return { isValid: false, error: 'Le fichier doit être une vidéo' };
    }

    // Vérifier la taille (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return { isValid: false, error: 'La taille de la vidéo ne doit pas dépasser 50MB' };
    }

    // Vérifier les formats acceptés
    const acceptedFormats = [
      'video/mp4', 
      'video/webm', 
      'video/ogg', 
      'video/avi', 
      'video/mov', 
      'video/quicktime'
    ];
    if (!acceptedFormats.includes(file.type)) {
      return { 
        isValid: false, 
        error: 'Format vidéo non supporté. Utilisez MP4, WebM, OGG, AVI ou MOV' 
      };
    }

    // Vérifier la durée de la vidéo
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        const duration = video.duration;
        
        // Limite de 2 minutes (120 secondes)
        const maxDuration = 120;
        if (duration > maxDuration) {
          resolve({ 
            isValid: false, 
            error: 'La durée de la vidéo ne doit pas dépasser 2 minutes',
            duration 
          });
        } else {
          resolve({ isValid: true, duration });
        }
        
        // Nettoyer
        URL.revokeObjectURL(video.src);
      };
      
      video.onerror = () => {
        resolve({ 
          isValid: false, 
          error: 'Impossible de lire la vidéo. Vérifiez le format.' 
        });
        URL.revokeObjectURL(video.src);
      };
      
      video.src = URL.createObjectURL(file);
    });
  }

  /**
   * Génère une miniature (thumbnail) à partir d'une vidéo
   * @param file - Le fichier vidéo
   * @param timeInSeconds - Temps en secondes où capturer la miniature (défaut: 1s)
   * @returns Promise<Blob | null> - La miniature sous forme de blob
   */
  static async generateThumbnail(file: File, timeInSeconds: number = 1): Promise<Blob | null> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        resolve(null);
        return;
      }
      
      video.onloadedmetadata = () => {
        // Définir les dimensions du canvas
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Aller au temps spécifié
        video.currentTime = Math.min(timeInSeconds, video.duration);
      };
      
      video.onseeked = () => {
        // Dessiner la frame courante sur le canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convertir en blob
        canvas.toBlob((blob) => {
          resolve(blob);
          URL.revokeObjectURL(video.src);
        }, 'image/jpeg', 0.8);
      };
      
      video.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(video.src);
      };
      
      video.src = URL.createObjectURL(file);
    });
  }

  /**
   * Obtient les métadonnées d'une vidéo (durée, dimensions, etc.)
   * @param file - Le fichier vidéo
   * @returns Promise<VideoMetadata | null>
   */
  static async getVideoMetadata(file: File): Promise<VideoMetadata | null> {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        const metadata: VideoMetadata = {
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
          size: file.size,
          type: file.type,
          name: file.name
        };
        
        resolve(metadata);
        URL.revokeObjectURL(video.src);
      };
      
      video.onerror = () => {
        resolve(null);
        URL.revokeObjectURL(video.src);
      };
      
      video.src = URL.createObjectURL(file);
    });
  }
}

// Interface pour les métadonnées vidéo
export interface VideoMetadata {
  duration: number; // en secondes
  width: number;
  height: number;
  size: number; // en bytes
  type: string; // MIME type
  name: string; // nom du fichier
}
